//
// Hook for checking user privileges across home and partner contexts.
// Exposes helpers to test privilege codes per context, enumerate
// tabbable contexts, and determine if privileges have been loaded.
//

import {
  useCallback,
  useMemo,
} from 'react';

import {
  useTownConfig,
  useAuth,
} from '../../stores';
import config from '../config';

// Hook for checking user privileges across home and partner contexts.
// Used by: SideMenu (show/hide menu items), PrivilegeGate (conditionally render UI),
//   ContextTabs (build partner/home tab list), HomePageSingleCase (per-case checks).
// privilegesMap: Map keyed by piid (or 'home'), values are { privileges: string[] }.
// piid: partner invite ID; omit or undefined for home context.
function usePrivileges() {
  const { townConfig } = useTownConfig();
  const {
    privilegesMap,
    getPrivileges,
    cstmrLbl,
    logoId,
  } = townConfig;
  const { auth } = useAuth();
  const { info = {} } = auth;
  const {
    cuid,
    partners,
  } = info;

  // True once privileges have been fetched (home entry exists in map)
  const privilegesReady = useMemo(
    () => privilegesMap.has('home'),
    [privilegesMap],
  );

  // Returns privilege list for a given context (piid or 'home')
  const getContextPrivileges = useCallback(
    (piid) => getPrivileges(piid),
    [getPrivileges],
  );

  // Checks if a single privilege code exists in a specific context
  const hasPrivilege = useCallback(
    (code, piid) => {
      const privs = getPrivileges(piid);
      return privs?.privileges?.includes(code) ?? false;
    },
    [getPrivileges],
  );

  // True if any of the given codes exists in any context (home or partners)
  const hasAnyPrivilege = useCallback(
    (codes) => {
      if (!codes?.length) return true;

      let found = false;
      privilegesMap.forEach((privList) => {
        if ((!found) && privList?.privileges?.length) {
          found = codes.some((code) => privList.privileges.includes(code));
        }
      });
      return found;
    },
    [privilegesMap],
  );

  // Returns all contexts (home + partners) as tab descriptors with labels and logo URLs.
  // Returns null if there are no partners (no tabs needed).
  const getTabbableContexts = useCallback(
    () => {
      if (!partners?.length) return null;

      return [
        {
          key: 'home',
          piid: undefined,
          lbl: cstmrLbl,
          logoId,
          logoUrl: (logoId && cuid)
            ? `${config.town.back.urlPrefix}${config.town.back.logos.get}${cuid}/${logoId}`
            : undefined,
        },
        ...partners.map((partner) => ({
          key: partner.piid,
          piid: partner.piid,
          lbl: partner.lbl,
          logoId: partner.logoId,
          logoUrl: partner.logoId
            ? `${partner.backUrl}/${config.town.back.logos.get}${partner.cuid}/${partner.logoId}`
            : undefined,
        })),
      ];
    },
    [partners, cstmrLbl, logoId, cuid],
  );

  // True if any of the given codes exists in the specified context
  const hasPrivilegeInContext = useCallback(
    (codes, piid) => {
      if (!codes?.length) return true;
      const privs = privilegesMap.get(piid ?? 'home');
      return codes.some((code) => privs?.privileges?.includes(code));
    },
    [privilegesMap],
  );

  // Returns contexts where the user has at least one of the given privilege codes
  const getAccessibleContexts = useCallback(
    (codes) => {
      const contexts = [];

      // Check home context
      const homePrivs = privilegesMap.get('home');
      if (homePrivs?.privileges?.length
          && codes.some((code) => homePrivs.privileges.includes(code))) {
        contexts.push({
          key: 'home',
          piid: undefined,
          lbl: null,
          logoId: null,
        });
      }

      // Check partner contexts
      if (partners?.length) {
        partners.forEach((partner) => {
          const partnerPrivs = privilegesMap.get(partner.piid);
          if (partnerPrivs?.privileges?.length
              && codes.some((code) => partnerPrivs.privileges.includes(code))) {
            contexts.push({
              key: partner.piid,
              piid: partner.piid,
              lbl: partner.lbl,
              logoId: partner.logoId,
            });
          }
        });
      }

      return contexts;
    },
    [privilegesMap, partners],
  );

  return {
    privilegesReady,
    getContextPrivileges,
    hasPrivilege,
    hasAnyPrivilege,
    getAccessibleContexts,
    getTabbableContexts,
    hasPrivilegeInContext,
  };
}

export default usePrivileges;
